/**
 * Основной Type2X Rating.
 *
 * Формула версионируется и хранится в конфигурации, общей для клиента и
 * сервера: расхождение между показанным и засчитанным рейтингом подорвало бы
 * доверие к таблицам лидеров (ТЗ FR-LB-03).
 *
 * Слагаемые и их доли заданы требованием (ТЗ FR-LB-02): рост относительно
 * baseline, текущий подтверждённый навык, точность, сохранение с
 * регулярностью и разнообразие режимов. Сами доли лежат в `rating.v1.json`.
 *
 * Числа формулы и параметры отложенной проверки объявлены один раз
 * (ТЗ FR-EN-46): клиент импортирует конфигурацию напрямую, сервер читает копию
 * внутри своего пакета, а совпадение копии с источником проверяет
 * `npm run check:config`.
 *
 * В рейтинг попадает только подтверждённый результат (ТЗ FR-LB-05).
 */

import ratingConfig from './config/rating.v1.json' with { type: 'json' };

import { clamp01, smoothGrowth } from './normalize.js';

/** Веса слагаемых рейтинга. */
export interface RatingWeights {
  readonly growth: number;
  readonly skill: number;
  readonly accuracy: number;
  readonly retention: number;
  readonly variety: number;
}

/** Конфигурация рейтинга. */
export interface RatingConfig {
  readonly version: string;
  readonly weights: RatingWeights;
  /**
   * Режимы, которые считаются в разнообразие.
   *
   * Список — это выпущенные режимы, а не все задуманные: делить на четырнадцать
   * режимов, из которых работает половина, значит показывать пользователю
   * заведомо недостижимое слагаемое. Список растёт по мере выпуска режимов
   * (ТЗ §6).
   */
  readonly varietyModes: readonly string[];
  readonly growthCap: number;
  readonly minCreditedBaselineWpm: number;
  readonly minVerifiedAccuracy: number;
  readonly minVerifiedDurationMs: number;
  readonly minVerifiedCharacters: number;
  /** Минимальный перерыв, после которого проверка считается отложенной. */
  readonly retentionMinDelayHours: number;
  /** Через сколько часов отложенную проверку имеет смысл предложить. */
  readonly retentionCheckDelayHours: number;
  /** Допустимое колебание скорости, при котором навык считается сохранённым. */
  readonly retentionSpeedToleranceRatio: number;
  /**
   * Допустимое падение точности в абсолютных долях.
   *
   * Для точности множитель не годится: она живёт у самой единицы, и доля в
   * десять процентов означала бы падение с 0.96 до 0.86 — ниже порога,
   * при котором результат вообще принимается.
   */
  readonly retentionAccuracyToleranceDelta: number;
  /** После какого срока прежний результат перестаёт быть точкой отсчёта. */
  readonly retentionExpiryHours: number;
}

export const RATING_CONFIG: RatingConfig = ratingConfig;

/** Исходные данные для расчёта рейтинга. */
export interface RatingInput {
  /** Исходная скорость, WPM. */
  readonly baselineWpm: number;
  /** Подтверждённая текущая скорость, WPM. */
  readonly verifiedWpm: number;
  /** Точность подтверждённого теста, 0..1. */
  readonly verifiedAccuracy: number;
  /** Сохранение навыка при отложенной проверке, 0..1. */
  readonly retention: number;
  /** Разнообразие освоенных режимов, 0..1. */
  readonly variety: number;
}

/** Вклад каждого слагаемого в итог. */
export interface RatingBreakdown {
  readonly growth: number;
  readonly skill: number;
  readonly accuracy: number;
  readonly retention: number;
  readonly variety: number;
}

/** Результат расчёта. */
export interface RatingResult {
  readonly version: string;
  /** Итоговый рейтинг, 0..1000. */
  readonly rating: number;
  readonly breakdown: RatingBreakdown;
}

/**
 * Доля освоенных режимов, 0..1.
 *
 * Считается по режимам засчитанных сессий: разнообразие подтверждается
 * практикой, а не открытием экрана.
 *
 * @param modes режимы, в которых пользователь уже занимался;
 * @param config конфигурация рейтинга.
 */
export function varietyScore(
  modes: Iterable<string>,
  config: RatingConfig = RATING_CONFIG,
): number {
  const counted = new Set(config.varietyModes);
  if (counted.size === 0) return 0;

  const practised = new Set([...modes].filter((mode) => counted.has(mode)));
  return clamp01(practised.size / counted.size);
}

/** Максимальное значение рейтинга: целое число удобнее показывать. */
export const RATING_SCALE = 1000;

/** Скорость, при которой составляющая skill достигает максимума. */
export const SKILL_CEILING_WPM = 100;

/**
 * Считает основной рейтинг.
 *
 * Рост сглажен логарифмом и опирается на baseline не ниже минимального:
 * искусственно заниженный старт не даёт преимущества (ТЗ FR-LB-04).
 */
export function computeRating(
  input: RatingInput,
  config: RatingConfig = RATING_CONFIG,
): RatingResult {
  const creditedBaseline = Math.max(input.baselineWpm, config.minCreditedBaselineWpm);

  const breakdown: RatingBreakdown = {
    growth: smoothGrowth(input.verifiedWpm / creditedBaseline, config.growthCap),
    skill: clamp01(input.verifiedWpm / SKILL_CEILING_WPM),
    accuracy: clamp01(input.verifiedAccuracy),
    retention: clamp01(input.retention),
    variety: clamp01(input.variety),
  };

  const { weights } = config;
  const score =
    breakdown.growth * weights.growth +
    breakdown.skill * weights.skill +
    breakdown.accuracy * weights.accuracy +
    breakdown.retention * weights.retention +
    breakdown.variety * weights.variety;

  return {
    version: config.version,
    rating: Math.round(clamp01(score) * RATING_SCALE),
    breakdown,
  };
}

/** Сумма весов конфигурации. Используется для её проверки. */
export function ratingWeightsSum(weights: RatingWeights): number {
  return weights.growth + weights.skill + weights.accuracy + weights.retention + weights.variety;
}

/**
 * Проверяет, годится ли результат для рейтинга.
 *
 * Полная проверка выполняется на сервере: клиентская нужна, чтобы не
 * предлагать пользователю отправить заведомо незачётный результат.
 *
 * Длительность и объём — два способа померить одно и то же: достаточно ли
 * велика выборка. ТЗ FR-VT-01 требует «минимальную длительность или объём», а
 * код требовал обе границы сразу — и был строже требования.
 *
 * Стоило это дорого и ровно тем, ради кого рейтинг существует. Контрольный
 * текст — около трёхсот знаков; печатающий быстрее трёхсот знаков в минуту
 * заканчивал его меньше чем за минуту и получал отказ. Ниже трёхсот знаков в
 * минуту начинается ступень «Уверенный»: «Быстрый» и «Профессиональный» не
 * могли пройти контрольный тест вовсе, сколько бы раз ни пробовали.
 *
 * Правдоподобность скорости проверяет не эта граница, а оценка риска
 * (ТЗ FR-VT-02): невозможные интервалы, машинный ритм и недостижимый темп —
 * её дело, а не дело минимального объёма.
 */
export function meetsVerifiedRequirements(
  accuracy: number,
  durationMs: number,
  characters: number,
  config: RatingConfig = RATING_CONFIG,
): boolean {
  const bigEnough =
    durationMs >= config.minVerifiedDurationMs || characters >= config.minVerifiedCharacters;

  return accuracy >= config.minVerifiedAccuracy && bigEnough;
}
